import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { TextField } from '~/components/form/TextField';
import { Dialog } from '~/components/ui/Dialog';
import { Button } from '../../ui/buttons/Button';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onTypeCreated: (label: string) => void | Promise<void>;
  defaultValue?: string;
};

export function CreateContactTypeDialog({
  isOpen,
  onClose,
  onTypeCreated,
  defaultValue,
}: Props) {
  const [label, setLabel] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setLabel(defaultValue?.trim() ?? '');
      setErrorMessage('');
    }
  }, [isOpen, defaultValue]);

  const handleCreate = async () => {
    const value = label.trim();
    if (!value) return;

    setErrorMessage('');
    setIsCreating(true);
    try {
      await onTypeCreated(value);
      handleClose();
    } catch (error) {
      toast.error('Failed to create contact type. Please try again.');
      console.error('Failed to create contact type:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to create contact type. Please try again.';
      setErrorMessage(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setLabel('');
    setIsCreating(false);
    setErrorMessage('');
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && label.trim()) {
      e.preventDefault();
      e.stopPropagation();
      handleCreate();
    }
  };

  return (
    <Dialog
      show={isOpen}
      title='Create New Contact Type'
      onClose={handleClose}
      isLoading={isCreating}
    >
      <div className='p-5'>
        <div className='mb-4'>
          <TextField
            name='contactType'
            label='Contact Type'
            value={label}
            onChange={(e) => {
              setLabel((e.target as HTMLInputElement).value);
              if (errorMessage) setErrorMessage('');
            }}
            onKeyDown={handleKeyDown}
            placeholder='Enter contact type name...'
            autoFocus
            error={errorMessage}
          />
        </div>

        <div className='flex justify-end gap-3'>
          <Button
            variant='secondary'
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!label.trim() || isCreating}
            loading={isCreating}
          >
            Create
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
